///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import type { IViewer } from "../viewer/IViewer";
import { IDragger, IDraggerProvider, IDraggersMap, IDraggersRegistry } from "./IDraggers";

class DraggersRegistry implements IDraggersRegistry {
  private readonly _draggers = new Map<string, IDraggerProvider>();

  registerDragger(name: string, provider: IDraggerProvider): void {
    this._draggers.set(name, provider);
  }

  registerDraggerAlias(name: string, alias: string): void {
    const provider = this.getDragger(name);
    if (provider) this.registerDragger(alias, (viewer: IViewer) => provider(viewer));
  }

  getDragger(name: string): IDraggerProvider | undefined {
    return this._draggers.get(name);
  }

  getDraggers(): IDraggersMap {
    const map = new Map<string, IDraggerProvider>();
    this._draggers.forEach((value, key) => map.set(key, value));
    return map;
  }

  createDragger(name: string, viewer: IViewer): IDragger | null {
    const provider = this.getDragger(name);
    if (!provider) return null;

    const dragger = provider(viewer);
    dragger.name = name;

    return dragger;
  }
}

const _draggersRegistry = new Map<string, DraggersRegistry>();

function draggersRegistry(viewerType = ""): IDraggersRegistry {
  let result = _draggersRegistry.get(viewerType);
  if (!result) {
    result = new DraggersRegistry();
    _draggersRegistry.set(viewerType, result);
  }
  return result;
}

export { draggersRegistry };
